/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import ImageDataLoader from "App/utils/textures/image_data_loader";
import ImageDataLoadersFactory from "App/utils/textures/image_data_loaders_factory";
import DOMImageDataLoader from "App/utils/textures/dom_image_loader";
import TextureDataHandler from "App/utils/textures/texture_data_handler";
import {Base64ToUint8Array} from "App/utils/utils";
import IScene from "js_interface/src/scene_interface";

export default
class CanvasTextureDictionary {
    private static UIDGenerator: number = 1;
    private _UID: number = CanvasTextureDictionary.UIDGenerator++;
    private _sceneAPI: IScene;
    private _dictionary: {} = {};
    private _canvasDimension: {
        cols: number,
        rows: number
    };
    private _canvasDimensionPx: {
        width: number,
        height: number
    };
    private _glyphMaxSizePx: {
        width: number,
        height: number
    };

    private _canvasUsageMap: any;
    private _canvas: HTMLCanvasElement;
    private _context: CanvasRenderingContext2D;
    private _canvasTextureUri: string;
    private _textureUpdateScheduled: boolean = false;
    private _DOMImageDataLoader: ImageDataLoader = null;

    constructor(aSceneAPI: IScene) {
        this._sceneAPI = aSceneAPI;

        // each canvas can handle 128 characters
        this._canvasDimension = {cols: 16, rows: 8}; // cols, rows
        this._canvasDimensionPx = {width: 2048, height: 1024};
        this._glyphMaxSizePx = {
            width: this._canvasDimensionPx.width / this._canvasDimension.cols,
            height: this._canvasDimensionPx.height / this._canvasDimension.rows
        };

        // create canvas usage map
        this._canvasUsageMap = {rows: []};
        for (let r = 0; r < this._canvasDimension.rows; r++) {
            this._canvasUsageMap.rows.push({cols: []});
            for (let c = 0; c < this._canvasDimension.cols; c++) {
                this._canvasUsageMap.rows[r].cols.push(0);
            }
        }

        this._canvas = document.createElement('canvas');
        this._canvas.style.backgroundColor = "#ffffff;";
        this._canvas.width = this._canvasDimensionPx.width;
        this._canvas.height = this._canvasDimensionPx.height;
        this._context = this._canvas.getContext("2d");

        this._canvasTextureUri = this._sceneAPI.getDocument().createLocalResourceUri();
    }

    public getUID(): number {
        return this._UID;
    };

    public getCanvas(): HTMLCanvasElement {
        return this._canvas;
    }

    public updateTexture(): boolean {
        if (this._textureUpdateScheduled) {
            this._createDOMImageDataLoader();
            this._textureUpdateScheduled = false;
        }

        if (this._DOMImageDataLoader && this._DOMImageDataLoader.isReady()) {
            this._sceneAPI.getDocument().notifySetTextureData(
                this._canvasTextureUri,
                this._DOMImageDataLoader
            );
            this._DOMImageDataLoader = null;
        }

        return this._DOMImageDataLoader !== null;
    }

    private _createDOMImageDataLoader(): void {
        let textureProperty = this._getTextureProperty();
        this._DOMImageDataLoader = ImageDataLoadersFactory.create(
            DOMImageDataLoader.TypeName,
            null,
            TextureDataHandler.createTextureProperty(
                textureProperty.url,
                textureProperty.relativeUrl ? textureProperty.relativeUrl : textureProperty.url,
                textureProperty.width,
                textureProperty.height,
                textureProperty.offset,
                textureProperty.size,
                textureProperty.tiled,
                textureProperty.alpha,
                DOMImageDataLoader.TypeName
            )
        );

        this._DOMImageDataLoader.parse(textureProperty.compressedData);
        this._DOMImageDataLoader.prepareData();
    }

    private _getTextureProperty(): any {
        let base64Data = this._canvas.toDataURL();
        let dataOffset = base64Data.indexOf("base64,");
        if (dataOffset !== -1) {
            base64Data = base64Data.substring(dataOffset + "base64,".length);
        }

        return {
            url: this._canvasTextureUri,
            width: this._canvasDimensionPx.width,
            height: this._canvasDimensionPx.height,
            alpha: true,
            offset: 0,
            size: 0,
            tiled: false,
            compressedData: Base64ToUint8Array(base64Data)
        };
    }

    private getFreePosition(): { row: number, col: number } {
        for (let r = 0; r < this._canvasDimension.rows; r++) {
            for (let c = 0; c < this._canvasDimension.cols; c++) {
                if (this._canvasUsageMap.rows[r].cols[c] === 0) {
                    return {row: r, col: c};
                }
            }
        }

        return null;
    }

    private _getTextHeight(aFont: string, aFontSize: number): number {
        let text: any = document.createElement('span');
        text.innerHTML = 'Hg';
        text.style.fontFamily = aFont;
        text.style.fontSize = aFontSize + 'px';

        let block: any = document.createElement('div');
        block.style.display = 'inline-block';
        block.style.width = '1px';
        block.style.height = '1px';

        let div: HTMLDivElement = document.createElement('div');
        div.appendChild(text);
        div.appendChild(block);
        document.body.appendChild(div);

        let result: any = {};
        try {
            block.offset = {
                top: block.getBoundingClientRect().top + document.body.scrollTop,
                left: block.getBoundingClientRect().left + document.body.scrollLeft
            };

            text.offset = {
                top: text.getBoundingClientRect().top + document.body.scrollTop,
                left: text.getBoundingClientRect().left + document.body.scrollLeft
            };

            block.style.verticalAlign = 'baseline';
            // @ts-ignore
            result.ascent = block.offset.top - text.offset.top;

            block.style.verticalAlign = 'bottom';
            // @ts-ignore
            result.height = block.offset.top - text.offset.top;

            result.descent = result.height - result.ascent;
        } finally {
            document.body.removeChild(div);
        }

        return result.height;
    }

    private _renderCharToDictionary(aChar: string, aMetric: any, aFontStyle: string, aFontSize: number, aFontName: string, aOutlineSize: number): void {
        this._context.font = aFontStyle + " " + aFontSize + "pt " + aFontName;

        aMetric.pixelPosition = {
            left: aMetric.position.col * this._glyphMaxSizePx.width + (this._glyphMaxSizePx.width / 2.0),
            top: aMetric.position.row * this._glyphMaxSizePx.height + (this._glyphMaxSizePx.height / 2.0)
        };

        aMetric.normalizedPosition = {
            left: (aMetric.pixelPosition.left - 2) / this._canvasDimensionPx.width,
            top: (aMetric.pixelPosition.top + 7) / this._canvasDimensionPx.height
        };

        // fill text - draw a black outline first
        this._context.fillStyle = 'rgb(0, 0, 0)'; // black is identifier of outline for shader
        this._context.fillText(aChar, aMetric.pixelPosition.left - 0, aMetric.pixelPosition.top - aOutlineSize);
        this._context.fillText(aChar, aMetric.pixelPosition.left - aOutlineSize, aMetric.pixelPosition.top - 0);
        this._context.fillText(aChar, aMetric.pixelPosition.left + aOutlineSize, aMetric.pixelPosition.top + 0);
        this._context.fillText(aChar, aMetric.pixelPosition.left + 0, aMetric.pixelPosition.top + aOutlineSize);

        aMetric.pixelDimension = {
            width: this._context.measureText(aChar).width,
            height: this._getTextHeight(aFontName, aFontSize)
        };

        aMetric.normalizedDimension = {
            width: (aMetric.pixelDimension.width + 4) / this._canvasDimensionPx.width,
            height: (aMetric.pixelDimension.height + 9) / this._canvasDimensionPx.height
        };

        this._context.fillText(aChar, aMetric.pixelPosition.left - aOutlineSize, aMetric.pixelPosition.top - aOutlineSize);
        this._context.fillText(aChar, aMetric.pixelPosition.left - aOutlineSize, aMetric.pixelPosition.top + aOutlineSize);
        this._context.fillText(aChar, aMetric.pixelPosition.left + aOutlineSize, aMetric.pixelPosition.top - aOutlineSize);
        this._context.fillText(aChar, aMetric.pixelPosition.left + aOutlineSize, aMetric.pixelPosition.top + aOutlineSize);

        this._context.fillStyle = 'rgb(255, 255, 255)'; // white color is identifier of char for shader

        // fill text
        this._context.fillText(
            aChar,
            aMetric.pixelPosition.left,
            aMetric.pixelPosition.top
        );

        //console.log("new char '" + aChar + "':" + aMetric.pixelPosition.left + ", " + aMetric.pixelPosition.top);
    }

    public addChar(aChar: string, aFontStyle: string, aFontSize: number, aFontName: string, aOutlineSize: number): any {
        // first look for the aChar in the dictionary
        let metric = this._dictionary[aChar];
        if (metric === undefined) {

            let position = this.getFreePosition();
            if (!position) {
                return null; // no free position for the char
            }

            metric = {
                position: position,
                pixelPosition: "will be determined in _renderCharToDictionary",
                pixelDimension: "will be determined in _renderCharToDictionary",
                normalizedPosition: "will be determined in _renderCharToDictionary in interval <0,1>",
                normalizedDimension: "will be determined in _renderCharToDictionary in interval <0,1>",
                canvasTextureUri: this._canvasTextureUri,
                canvasTextureWidth: this._canvasDimensionPx.width,
                canvasTextureHeight: this._canvasDimensionPx.height
            };

            this._dictionary[aChar] = metric;

            this._renderCharToDictionary(aChar, metric, aFontStyle, aFontSize, aFontName, aOutlineSize);

            this._textureUpdateScheduled = true;
        }

        this._canvasUsageMap.rows[metric.position.row].cols[metric.position.col]++;

        return metric;
    }
}
